import { useCallback } from 'react'
import axios from 'axios'

import { PropertyEndpoint } from 'constants/API'
import type { RealEstatePropertyDTO } from 'models/property'
import type { PurchaseSessionStatusV2DTO } from 'models/purchaseFlowRevamp'
import { type APIErrorReportingConfig, useAPI, useAPIV2 } from 'utils/API'

enum Endpoints {
  SESSION_STATUS = '/workspace/{workspaceId}/sessions/{sessionId}',
}

/** What the server knows about a session URL: ended in an order, still open on a property, or not the caller's. */
export type ServerSessionLookup =
  | { kind: 'ordered', orderId: number | null }
  | { kind: 'open', property: RealEstatePropertyDTO }
  /** `session`: the server does not know the session for the caller; `property`: its property is gone. */
  | { kind: 'unknown', reason: 'session' | 'property' }

/** 404: not the caller's session or property. 400: the id in the link is not a UUID, which also means no such session. */
const MISSING_STATUSES = [400, 404]

const isMissing = (error: unknown) => axios.isAxiosError(error) && MISSING_STATUSES.includes(error.response?.status ?? 0)

/** A missing session or property is an expected answer, so it is kept out of Sentry. */
const IGNORE_MISSING: APIErrorReportingConfig = { ignoreErrorFactory: isMissing }

/**
 * Asks the server about a session the browser holds no draft for (`GET /workspace/{ws}/sessions/{id}`), and for
 * an open one loads its property. A 404 or 400 from either call is `unknown`; any other failure rejects.
 *
 * @example
 * const lookUp = useServerSessionLookup()
 * const result = await lookUp(workspaceId, sessionId)
 */
export function useServerSessionLookup(): (workspaceId: string, sessionId: string) => Promise<ServerSessionLookup> {
  const apiV2 = useAPIV2<Endpoints>()
  const api = useAPI<PropertyEndpoint>()

  return useCallback(async (workspaceId, sessionId) => {
    let status: PurchaseSessionStatusV2DTO
    try {
      status = await apiV2.get<PurchaseSessionStatusV2DTO>(Endpoints.SESSION_STATUS, { workspaceId, sessionId }, false, undefined, IGNORE_MISSING)
    }
    catch (error) {
      if (isMissing(error)) return { kind: 'unknown', reason: 'session' }
      throw error
    }
    if (status.status === 'ORDERED') return { kind: 'ordered', orderId: status.orderPublicId }

    try {
      const property = await api.get<RealEstatePropertyDTO>(
        PropertyEndpoint.GET_PROPERTY_DETAIL,
        { workspaceId, propertyId: status.realEstatePropertyId },
        false,
        undefined,
        IGNORE_MISSING
      )
      return { kind: 'open', property }
    }
    catch (error) {
      if (isMissing(error)) return { kind: 'unknown', reason: 'property' }
      throw error
    }
  }, [apiV2, api])
}
